import { useCallback, useEffect, useRef, useState } from 'react';
import { useSpeechRecognition } from 'react-speech-recognition';
import SpeechRecognition from 'react-speech-recognition';
import { registerActiveDictationStop } from '../dictationSessionRegistry';

/**
 * Cloud speech polyfills (e.g. Azure) may not release the mic until abort settles;
 * see https://github.com/JamesBrill/react-speech-recognition/issues/148
 */
const ABORT_RECOGNITION_TIMEOUT_MS = 3000;

/** A stop that still has a phrase to flush normally ends within a few hundred ms. */
const STOP_RECOGNITION_TIMEOUT_MS = 1500;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function raceAbortSpeechRecognition(): Promise<void> {
  await Promise.race([SpeechRecognition.abortListening(), wait(ABORT_RECOGNITION_TIMEOUT_MS)]);
}

/**
 * Ends recognition for Done. The Azure polyfill only ends a stop once another result arrives, so a
 * stop with nothing left to recognize never ends: abort instead, and abort a stop that takes too long.
 */
async function finishSpeechRecognition(hasPendingSpeech: boolean): Promise<void> {
  if (!hasPendingSpeech) {
    await raceAbortSpeechRecognition();
    return;
  }

  // Unlike abort, stop lets the engine flush pending audio and deliver the final result.
  const stopped = SpeechRecognition.stopListening();
  const didStop = await Promise.race([stopped.then(() => true), wait(STOP_RECOGNITION_TIMEOUT_MS).then(() => false)]);
  if (didStop) return;

  // The library already counts itself as stopped, so its abortListening() would not reach the recognizer.
  SpeechRecognition.getRecognition()?.abort();
  await Promise.race([stopped, wait(ABORT_RECOGNITION_TIMEOUT_MS)]);
}

/**
 * Dictation hooks for the live-splice flow: `onStart` saves the field, the transcript is inserted
 * as it grows, then Done keeps it (`onCommit`) and Cancel restores it (`onCancel`).
 */
export interface DictationHandlers {
  /** Fires right after the mic starts and the transcript is reset to ''. Use to snapshot pre-dictation state. */
  onStart?: () => void;
  /** Fires when the user clicks Done. Caller keeps whatever spliced state it built up. */
  onCommit?: () => void;
  /** Fires when the user clicks Cancel. Caller should restore whatever they snapshotted in `onStart`. */
  onCancel?: () => void;
}

export function useDictation(handlers?: DictationHandlers) {
  const { transcript, interimTranscript, listening, browserSupportsSpeechRecognition, resetTranscript } =
    useSpeechRecognition();

  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isManuallyStopped, setIsManuallyStopped] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const interimTranscriptRef = useRef(interimTranscript);

  // Keep handlers in a ref so the callbacks stay stable.
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  useEffect(() => {
    interimTranscriptRef.current = interimTranscript;
  }, [interimTranscript]);

  // The final result and `end` arrive together, so Done waits one render for the final transcript
  // to reach the caller's effects before committing.
  const [renderTick, setRenderTick] = useState(0);
  const renderWaitersRef = useRef<Array<() => void>>([]);
  useEffect(() => {
    const waiters = renderWaitersRef.current;
    renderWaitersRef.current = [];
    waiters.forEach((resolve) => resolve());
  }, [renderTick]);

  const waitForRender = useCallback(
    () =>
      new Promise<void>((resolve) => {
        renderWaitersRef.current.push(resolve);
        setRenderTick((tick) => tick + 1);
      }),
    [],
  );

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    setAudioStream(null);
  }, []);

  const startListening = useCallback(async () => {
    if (!browserSupportsSpeechRecognition) return;

    setIsManuallyStopped(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      streamRef.current = stream;
      setAudioStream(stream);

      resetTranscript();

      SpeechRecognition.startListening({
        continuous: true,
        language: 'en-US',
      });

      // Fire onStart AFTER the transcript reset so the caller's snapshot
      // isn't racing a leftover transcript from a previous session.
      handlersRef.current?.onStart?.();
    } catch (err) {
      console.error('Failed to start dictation:', err);
      cleanupStream();
    }
  }, [browserSupportsSpeechRecognition, resetTranscript, cleanupStream]);

  const cancelListening = useCallback(() => {
    setIsManuallyStopped(true);

    // Restore the field before the async teardown, so it doesn't show stale text.
    handlersRef.current?.onCancel?.();

    // Cancel should discard any pending recognition result.
    cleanupStream();

    void raceAbortSpeechRecognition()
      .catch(() => {
        // recognition may already be idle
      })
      .finally(() => {
        resetTranscript();
        setIsFinalizing(false);
      });
  }, [cleanupStream, resetTranscript]);

  useEffect(() => {
    return registerActiveDictationStop(cancelListening);
  }, [cancelListening]);

  const confirmListening = useCallback(async () => {
    setIsManuallyStopped(true);
    setIsFinalizing(true);

    try {
      // An interim transcript is a phrase the engine has not finalized yet.
      await finishSpeechRecognition(interimTranscriptRef.current !== '');
      await waitForRender();
    } catch (err) {
      console.error('Failed to finalize dictation:', err);
    } finally {
      // The field already has the transcript, so just notify.
      handlersRef.current?.onCommit?.();

      resetTranscript();
      cleanupStream();
      setIsFinalizing(false);
    }
  }, [resetTranscript, cleanupStream, waitForRender]);

  useEffect(() => {
    return () => cleanupStream();
  }, [cleanupStream]);

  // react-speech-recognition may not update listening synchronously on stop/abort;
  // isManuallyStopped makes the UI reflect the user's action immediately.
  const effectiveListening = listening && !isManuallyStopped;

  return {
    listening: effectiveListening,
    /** Live interim transcript — grows as the user speaks. Empty between sessions. */
    transcript,
    audioStream,
    isFinalizing,
    browserSupportsSpeechRecognition,
    startListening,
    cancelListening,
    confirmListening,
  };
}
